<template>
  <SectionContentProductShowcaseCard v-bind="props" />
</template>

<script setup>
const props = defineProps({
  headerIconSrc: {
    type: String,
    default: '/assets/images/icon-top-logo.png'
  },
  headerIconAlt: {
    type: String,
    default: 'Mindflow icon'
  },
  productTitle: {
    type: String,
    default: 'Mindflow — Instant Brain Fuel'
  },
  productSubtitle: {
    type: String,
    default: 'Functional Coffee Supplement'
  },
  shopButtonText: {
    type: String,
    default: 'Shop Now'
  },
  shopButtonHref: {
    type: String,
    default: 'https://lifeboostcoffee.com/products/mindflow-instant'
  },
  introContent: {
    type: String,
    default: `<p>Call it the Lifeboost brain health trifecta, call it delicious, call it a part of your daily routine…I call it a delicious brain biohack!</p>
<p>Each of these products are truly powerful ways to support the health of your brain.</p>
<p><a href="https://lifeboostcoffee.com/products/mindflow-instant" target="_blank" rel="noopener noreferrer"><strong>Mindflow</strong></a> is our instant functional coffee supplement. It's made with:</p>`
  },
  productImageSrc: {
    type: String,
    default: '/assets/images/mindflow-right-mens.png'
  },
  productImageAlt: {
    type: String,
    default: 'Lifeboost Mindflow functional coffee supplement pouch'
  },
  productImageWidth: {
    type: Number,
    default: 277
  },
  productImageHeight: {
    type: Number,
    default: 318
  },
  productImageRounded: {
    type: Boolean,
    default: true
  },
  ingredients: {
    type: Array,
    default: () => [
      {
        title: 'Alpha-GPC',
        description: 'A highly bioavailable form of choline studied for its role in supporting memory, attention, and neurotransmitter activity.',
        imageSrc: '/assets/images/alpha-gpc.png',
        imageAlt: 'Alpha-GPC powder'
      },
      {
        title: 'CognatiQ™ Coffee Fruit',
        description: 'A patented whole coffee fruit extract shown in clinical research to increase BDNF (brain derived neurotrophic factor), a protein tied to brain connectivity and resilience.',
        imageSrc: '/assets/images/gognatiq-mens.png',
        imageAlt: 'Coffee fruit cherries'
      },
      {
        title: 'Grass Fed Collagen',
        description: 'This provides structural proteins that help maintain healthy tissues throughout the body, including those important for brain and gut health.',
        imageSrc: '/assets/images/grass-fed-collagen.png',
        imageAlt: 'Grass fed collagen powder'
      },
      {
        title: 'Lion\'s Mane & Chaga',
        description: 'Dual adaptogens with antioxidant and neuroprotective properties, traditionally used to support cognitive function and immune balance.',
        imageSrc: '/assets/images/mane-chaga.png',
        imageAlt: 'Lion\'s mane and chaga mushrooms'
      },
      {
        title: 'Electrolytes',
        description: 'Essential minerals that aid hydration and nerve signaling, helping maintain mental performance under stress.',
        imageSrc: '/assets/images/electrolytes.png',
        imageAlt: 'Electrolytes'
      }
    ]
  },
  features: {
    type: Array,
    default: () => []
  },
  statCards: {
    type: Array,
    default: () => []
  },
  paddingTop: {
    type: String,
    default: 'default'
  },
  paddingBottom: {
    type: String,
    default: 'default'
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Showcase Product With Blocks',
  inputs: [
    {
      name: 'headerIconSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: '/assets/images/icon-top-logo.png'
    },
    {
      name: 'headerIconAlt',
      type: 'text',
      defaultValue: 'Mindflow icon'
    },
    {
      name: 'productTitle',
      type: 'richText',
      defaultValue: 'Mindflow — Instant Brain Fuel'
    },
    {
      name: 'productSubtitle',
      type: 'text',
      defaultValue: 'Functional Coffee Supplement'
    },
    {
      name: 'shopButtonText',
      type: 'text',
      defaultValue: 'Shop Now'
    },
    {
      name: 'shopButtonHref',
      type: 'text',
      defaultValue: 'https://lifeboostcoffee.com/products/mindflow-instant'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Call it the Lifeboost brain health trifecta, call it delicious, call it a part of your daily routine…I call it a delicious brain biohack!</p>
<p>Each of these products are truly powerful ways to support the health of your brain.</p>
<p><a href="https://lifeboostcoffee.com/products/mindflow-instant" target="_blank" rel="noopener noreferrer"><strong>Mindflow</strong></a> is our instant functional coffee supplement. It's made with:</p>`
    },
    {
      name: 'productImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/mindflow-right-mens.png'
    },
    {
      name: 'productImageAlt',
      type: 'text',
      defaultValue: 'Lifeboost Mindflow functional coffee supplement pouch'
    },
    {
      name: 'productImageRounded',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'ingredients',
      type: 'list',
      defaultValue: [
        {
          title: 'Alpha-GPC',
          description: 'A highly bioavailable form of choline studied for its role in supporting memory, attention, and neurotransmitter activity.',
          imageSrc: '/assets/images/alpha-gpc.png',
          imageAlt: 'Alpha-GPC powder'
        },
        {
          title: 'CognatiQ™ Coffee Fruit',
          description: 'A patented whole coffee fruit extract shown in clinical research to increase BDNF (brain derived neurotrophic factor), a protein tied to brain connectivity and resilience.',
          imageSrc: '/assets/images/gognatiq-mens.png',
          imageAlt: 'Coffee fruit cherries'
        },
        {
          title: 'Grass Fed Collagen',
          description: 'This provides structural proteins that help maintain healthy tissues throughout the body, including those important for brain and gut health.',
          imageSrc: '/assets/images/grass-fed-collagen.png',
          imageAlt: 'Grass fed collagen powder'
        },
        {
          title: 'Lion\'s Mane & Chaga',
          description: 'Dual adaptogens with antioxidant and neuroprotective properties, traditionally used to support cognitive function and immune balance.',
          imageSrc: '/assets/images/mane-chaga.png',
          imageAlt: 'Lion\'s mane and chaga mushrooms'
        },
        {
          title: 'Electrolytes',
          description: 'Essential minerals that aid hydration and nerve signaling, helping maintain mental performance under stress.',
          imageSrc: '/assets/images/electrolytes.png',
          imageAlt: 'Electrolytes'
        }
      ],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'description',
          type: 'longText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'features',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'iconSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
          defaultValue: ''
        },
        {
          name: 'iconAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'label',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'statCards',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'text',
          type: 'richText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    }
  ]
})
</script>
